import {
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    makeStyles,
    MessageBar,
    MessageBarBody,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import type { PlanwertKostenErloeseItem } from '../../models/PlanwertKostenErloeseItem';
import type { DataService } from '../../services/dataService';

type OrderRow = { kostentyp: string; neu: number; offen: number; abgeschlossen: number; summe: number };

type OrderStatusKey = 'neu' | 'offen' | 'abgeschlossen';

const STATUS_SUFFIXES: OrderStatusKey[] = ['neu', 'offen', 'abgeschlossen'];

const formatCurrency = (value: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);

const buildOrderRows = (items: PlanwertKostenErloeseItem[]): { rows: OrderRow[]; total: OrderRow } => {
    const byType = new Map<string, OrderRow>();

    items.forEach((item) => {
        const name = item.positionBezeichnung ?? '';
        const status = STATUS_SUFFIXES.find((s) => name.endsWith(`_${s}`));
        if (!status) return;

        const kostentyp = item.positionArt?.trim() || '-';
        let row = byType.get(kostentyp);
        if (!row) {
            row = { kostentyp, neu: 0, offen: 0, abgeschlossen: 0, summe: 0 };
            byType.set(kostentyp, row);
        }
        row[status] += item.betrag ?? 0;
    });

    const rows = [...byType.values()]
        .map((row) => ({ ...row, summe: row.abgeschlossen }))
        .sort((a, b) => a.kostentyp.localeCompare(b.kostentyp));

    const sumOf = (key: OrderStatusKey) => rows.reduce((sum, row) => sum + row[key], 0);
    const neu = sumOf('neu');
    const offen = sumOf('offen');
    const abgeschlossen = sumOf('abgeschlossen');
    const total: OrderRow = { kostentyp: 'Summe', neu, offen, abgeschlossen, summe: abgeschlossen };

    return { rows, total };
};

const useStyles = makeStyles({
    tableWrapper: {
        width: 'fit-content',
        minWidth: '50%',
        maxWidth: '100%',
        overflowX: 'auto',
    },
    placeholder: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
        fontSize: tokens.fontSizeBase300,
    },
});

const headerRowStyle = {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
};

const totalRowStyle = {
    fontWeight: 700,
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
};

const columns = [
    createTableColumn<OrderRow>({
        columnId: 'kostentyp',
        renderHeaderCell: () => 'Cost Type',
        renderCell: (item) => item.kostentyp,
    }),
    createTableColumn<OrderRow>({
        columnId: 'neu',
        renderHeaderCell: () => 'New',
        renderCell: (item) => formatCurrency(item.neu),
    }),
    createTableColumn<OrderRow>({
        columnId: 'offen',
        renderHeaderCell: () => 'Open',
        renderCell: (item) => formatCurrency(item.offen),
    }),
    createTableColumn<OrderRow>({
        columnId: 'abgeschlossen',
        renderHeaderCell: () => 'Completed',
        renderCell: (item) => formatCurrency(item.abgeschlossen),
    }),
    createTableColumn<OrderRow>({
        columnId: 'summe',
        renderHeaderCell: () => 'Total',
        renderCell: (item) => formatCurrency(item.summe),
    }),
];

type EckdatenOverviewOrdersProps = {
    dataService: DataService | null;
    projektId: number;
};

export const EckdatenOverviewOrders: React.FC<EckdatenOverviewOrdersProps> = ({ dataService, projektId }) => {
    const styles = useStyles();
    const [orders, setOrders] = useState<PlanwertKostenErloeseItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!dataService || !projektId) return;
        let cancelled = false;
        dataService
            .getProjektkostenListeAlleBestellungenOverview(projektId)
            .then((data) => {
                if (!cancelled) {
                    setOrders(data);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load orders');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [dataService, projektId]);

    if (loading) {
        return <Spinner size="tiny" label="Loading orders..." />;
    }
    if (error) {
        return (
            <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
        );
    }
    if (orders.length === 0) {
        return <div className={styles.placeholder}>No orders found.</div>;
    }

    const { rows, total } = buildOrderRows(orders);
    const tableRows = [...rows, total];

    return (
        <div className={styles.tableWrapper}>
            <DataGrid items={tableRows} columns={columns} getRowId={(item) => item.kostentyp} aria-label="Orders overview">
                <DataGridHeader>
                    <DataGridRow style={headerRowStyle}>
                        {({ renderHeaderCell, columnId }) => (
                            <DataGridHeaderCell key={columnId} style={{ fontWeight: 700 }}>
                                {renderHeaderCell()}
                            </DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody<OrderRow>>
                    {({ item, rowId }) => (
                        <DataGridRow<OrderRow> key={rowId} style={item === total ? totalRowStyle : undefined}>
                            {({ renderCell, columnId }) => <DataGridCell key={columnId}>{renderCell(item)}</DataGridCell>}
                        </DataGridRow>
                    )}
                </DataGridBody>
            </DataGrid>
        </div>
    );
};
